import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select as MuiSelect,
  Typography,
} from '@mui/material';
import React from 'react';

interface MultiSelectProps {
  options: { value: string; label: string }[];
  value: string[];
  onChange: (value: string[]) => void;
  label: string;
  min?: number;
  max?: number;
  helperText?: string;
  placeholder?: string;
  disabled?: boolean;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  label,
  min,
  max,
  helperText,
  placeholder,
  disabled = false,
}) => {
  const safeValue = Array.isArray(value) ? value : [];

  const getHelperText = () => {
    if (helperText) return helperText;
    if (!min && !max) return null;
    if (min === max) {
      return `Seleccione ${min} ${min === 1 ? 'opción' : 'opciones'}`;
    }
    if (min !== undefined && max !== undefined) {
      if (safeValue.length < min) {
        return `Debe seleccionar al menos ${min} ${min === 1 ? 'opción' : 'opciones'}`;
      }
      if (safeValue.length > max) {
        return `Debe seleccionar como máximo ${max} ${max === 1 ? 'opción' : 'opciones'}`;
      }
      return `Seleccione entre ${min} y ${max} ${max === 1 ? 'opción' : 'opciones'}`;
    }
    if (min !== undefined) {
      if (safeValue.length < min) {
        return `Debe seleccionar al menos ${min} ${min === 1 ? 'opción' : 'opciones'}`;
      }
      return `Seleccione al menos ${min} ${min === 1 ? 'opción' : 'opciones'}`;
    }
    if (max !== undefined) {
      if (safeValue.length > max) {
        return `Debe seleccionar como máximo ${max} ${max === 1 ? 'opción' : 'opciones'}`;
      }
      return `Seleccione como máximo ${max} ${max === 1 ? 'opción' : 'opciones'}`;
    }
    return '';
  };

  return (
    <FormControl fullWidth margin="dense">
      <InputLabel id={`${label.toLowerCase().replace(/\s+/g, '-')}-label`}>
        {label}
      </InputLabel>
      <MuiSelect
        labelId={`${label.toLowerCase().replace(/\s+/g, '-')}-label`}
        id={`${label.toLowerCase().replace(/\s+/g, '-')}-select`}
        multiple
        value={safeValue}
        label={label}
        onChange={(event) => onChange(event.target.value as string[])}
        renderValue={(selected) =>
          selected
            .map((value) => options.find((o) => o.value === value))
            .filter((o): o is { value: string; label: string } => Boolean(o))
            .map((o) => o.label)
            .join(', ')
        }
        placeholder={placeholder}
        disabled={disabled}
      >
        {options.map((o) => {
          const isSelected = safeValue.indexOf(o.value) > -1;
          const wouldExceedMax =
            max !== undefined && !isSelected && safeValue.length >= max;

          return (
            <MenuItem key={o.value} value={o.value} disabled={wouldExceedMax}>
              <Checkbox checked={isSelected} />
              <ListItemText primary={o.label} />
            </MenuItem>
          );
        })}
      </MuiSelect>
      {getHelperText() && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, ml: 1.5 }}
        >
          {getHelperText()}
        </Typography>
      )}
    </FormControl>
  );
};

